import React, { lazy, Suspense, useEffect } from 'react';
import { useAnalytics } from 'use-analytics';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { PrivateRoute } from 'components';
import { observer } from 'mobx-react-lite';
import { authStore, permissionsStore } from './stores';

const ProfileScreen = lazy(() => import('screens/ProfileScreen'));
const CustomerRoute = lazy(() => import('screens/customers/CustomerRoute'));
const NewCustomersScreen = lazy(() =>
  import('screens/newCustomers').then((m) => ({ default: m.CustomersRouter })),
);
const OrganizerScreen = lazy(() => import('screens/organizer/OrganizerScreen'));
const NewOrganizerScreen = lazy(() =>
  import('screens/newOrganizer').then((m) => ({ default: m.OrganizerRouter })),
);
const PromisesScreen = lazy(() =>
  import('screens/promises').then((m) => ({ default: m.PromisesRouter })),
);
const MapScreen = lazy(() => import('screens/map/MapScreen'));
const DeliveriesScreen = lazy(
  () => import('screens/delivery/DeliveriesScreen'),
);
const FinanceScreen = lazy(() =>
  import('./screens/finance/ui/FinanceScreen').then((m) => ({
    default: m.FinanceScreen,
  })),
);
const CustomerScreen = lazy(() => import('screens/customers/CustomerScreen'));
const NewContactScreen = lazy(
  () => import('screens/customers/NewContactScreen'),
);
const ScanContactScreen = lazy(
  () => import('screens/customers/ScanContactScreen'),
);
const ContactScreen = lazy(() => import('screens/customers/ContactScreen'));
const CascadeRoute = lazy(() => import('screens/cascade/CascadeRoute'));
const ConstructionCardRoute = lazy(
  () => import('screens/constructionCard/ConstructionCardRoute'),
);
const ProductRoute = lazy(() => import('screens/product/ProductRoute'));
const AwaitForInvoiceScreen = lazy(() =>
  import('screens/signals/pages/await-for-invoice').then((m) => ({
    default: m.AwaitForInvoice,
  })),
);
const SignInScreen = lazy(() => import('screens/auth/SignInScreen'));
const NotFoundScreen = lazy(() => import('screens/NotFoundScreen'));
const DppMetricsScreen = lazy(
  () => import('screens/dppMetrics/DppMetricsScreen'),
);

const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    height="100vh"
  >
    <CircularProgress />
  </Box>
);

const Routes = observer(() => {
  const location = useLocation();
  const analytics = useAnalytics();
  const { isLoggedIn } = authStore;

  useEffect(() => {
    analytics.page();
  }, [location]); // eslint-disable-line

  const { forcedRoute } = permissionsStore;
  const shouldRedirectToForcedRoute =
    isLoggedIn &&
    permissionsStore.isInitialized &&
    location.pathname !== '/dpp-metrics' &&
    Boolean(forcedRoute);
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Switch>
        <PrivateRoute path="/dpp-metrics" exact>
          <DppMetricsScreen />
        </PrivateRoute>
        {shouldRedirectToForcedRoute && forcedRoute && (
          <Route path="*">
            <Redirect to={forcedRoute} />
          </Route>
        )}
        <PrivateRoute path="/" exact>
          <ProfileScreen />
        </PrivateRoute>
        <PrivateRoute path="/customers">
          <CustomerRoute />
        </PrivateRoute>
        <PrivateRoute path="/finance">
          <FinanceScreen apiPath="/external_data/finances/" />
        </PrivateRoute>
        <PrivateRoute path="/juncer_finances">
          <FinanceScreen apiPath="/external_data/juncer_finances/" />
        </PrivateRoute>
        <PrivateRoute path="/customers_new">
          <NewCustomersScreen />
        </PrivateRoute>
        <PrivateRoute path="/organizer">
          <OrganizerScreen />
        </PrivateRoute>
        <PrivateRoute path="/organizer_new">
          <NewOrganizerScreen />
        </PrivateRoute>
        <PrivateRoute path="/promises">
          <PromisesScreen />
        </PrivateRoute>
        <PrivateRoute path="/map">
          <MapScreen />
        </PrivateRoute>
        <PrivateRoute path="/deliveries">
          <DeliveriesScreen />
        </PrivateRoute>
        <PrivateRoute path="/contacts" exact>
          <CustomerScreen />
        </PrivateRoute>
        <PrivateRoute path="/contacts/new" exact>
          <NewContactScreen />
        </PrivateRoute>
        <PrivateRoute path="/contacts/scan" exact>
          <ScanContactScreen />
        </PrivateRoute>
        <PrivateRoute path="/contacts/:contactId" exact>
          <ContactScreen />
        </PrivateRoute>
        <PrivateRoute path="/cascades">
          <CascadeRoute />
        </PrivateRoute>
        <PrivateRoute path="/constructionCard">
          <ConstructionCardRoute />
        </PrivateRoute>
        <PrivateRoute path="/products">
          <ProductRoute />
        </PrivateRoute>
        <PrivateRoute path="/signals/await-for-invoice">
          <AwaitForInvoiceScreen />
        </PrivateRoute>
        <Route path="/signin" exact>
          <SignInScreen />
        </Route>
        <Route path="*">
          <NotFoundScreen />
        </Route>
      </Switch>
    </Suspense>
  );
});

export default Routes;
